import { useEffect } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';

import { DefaultSpinnerCenter } from '@components/Loader/Loader';
import { ConfigureNumbers } from '@components/Order/Numbers/CheckoutNumbers/ConfigureNumbers';
import ProfilesForPlan from '@components/Order/Numbers/CheckoutNumbers/ProfilesForPlan';
import { Page500 } from '@containers/ErrorPages';
import { cartApi } from '@reducers/order';
import { planDetailsApi } from '@reducers/plan';
import { skipToken } from '@reduxjs/toolkit/query';

const ApplyToAll = () => {
  const { values, setFieldValue } = useFormikContext();
  useEffect(() => {
    Object.keys(values.dids).forEach((item) => {
      setFieldValue(`dids.${item}.profileID`, values.all.profileID);
    });
  }, [values.all.profileID]);
  useEffect(() => {
    Object.keys(values.dids).forEach((item) =>
      setFieldValue(`dids.${item}.didServiceType`, values.all.didServiceType)
    );
  }, [values.all.didServiceType]);
  useEffect(() => {
    Object.keys(values.dids).forEach((item) =>
      setFieldValue(`dids.${item}.trunkID`, values.all.trunkID)
    );
  }, [values.all.trunkID]);
};

const CheckoutForPlan = ({ queries = {} }) => {
  const {
    useCartQuery,
    useSaveCartBackordersMutation,
    useSaveCartDidsMutation,
  } = queries;
  const { id, type, prefixID } = useParams();
  const navigate = useNavigate();

  const queryArg = id && prefixID ? { id, type, prefixID } : skipToken;
  const cart = useCartQuery(queryArg);

  const profiles =
    planDetailsApi.useGetProfilesAgainstRequirementsQuery(queryArg);

  const saveDids = useSaveCartDidsMutation();
  const saveBackorders = useSaveCartBackordersMutation();

  const isLoading =
    cart.isLoading ||
    cart.isFetching ||
    profiles.isLoading ||
    profiles.isFetching;
  if (isLoading) {
    return <DefaultSpinnerCenter />;
  }
  if (cart.isError || profiles.isError) {
    return <Page500 />;
  }
  if (cart.data && cart.data.length === 0) {
    return navigate('/');
  }

  const didsObjects = {};
  const schemaObjects = {};
  cart.data.forEach((item) => {
    didsObjects[`did-${item.id}`] = {
      ...item,
      profileID: '',
      trunkID: '',
      didServiceType: '',
    };
    schemaObjects[`did-${item.id}`] = Yup.object().shape({
      profileID: Yup.string().label('Profile').required(),
      trunkID: Yup.string().label('Trunk').required(),
      didServiceType: Yup.string().label('DID Service type').required(),
    });
  });
  const validationSchema = Yup.object().shape({
    dids: Yup.object().shape({ ...schemaObjects }),
  });
  const onSubmit = async (values, actions) => {
    const [triggerDids] = saveDids;
    const [triggerBackorders] = saveBackorders;

    actions.setSubmitting(true);
    function filterObject(obj, callback) {
      return Object.fromEntries(
        Object.entries(obj).filter(([key, val]) => callback(key, val))
      );
    }
    const didData = filterObject(
      values.dids,
      (_, { is_backorder: isBackorder }) => !isBackorder
    );
    const backorderData = filterObject(
      values.dids,
      (_, { is_backorder: isBackorder }) => isBackorder
    );
    const getRedirectPath = () =>
      values.checkout ? '/cart/checkout' : '/order/numbers';
    if (Object.entries(didData).length > 0) {
      triggerDids({ data: didData, id, type, prefixID })
        .unwrap()
        .then(() => {
          navigate(getRedirectPath());
        })
        .catch((error) => {
          actions.setErrors(error);
          actions.setSubmitting(false);
        });
    }
    if (Object.entries(backorderData).length > 0) {
      triggerBackorders({ data: backorderData, id, type, prefixID })
        .unwrap()
        .then(() => {
          navigate(getRedirectPath());
        })
        .catch((error) => {
          actions.setErrors(error);
          actions.setSubmitting(false);
        });
    }
  };
  return (
    <>
      <Row style={{ marginBottom: '10px' }}>
        <Col md={12}>
          <Formik
            validationSchema={validationSchema}
            initialValues={{
              checkout: true,
              all: { profileID: '', trunkID: '', didServiceType: '' },
              dids: didsObjects,
            }}
            onSubmit={onSubmit}
          >
            {({ handleSubmit }) => (
              <Form onSubmit={handleSubmit}>
                <ConfigureNumbers data={cart.data} queries={queries} />
                <ApplyToAll />
              </Form>
            )}
          </Formik>
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <ProfilesForPlan data={profiles.data} />
        </Col>
      </Row>
    </>
  );
};

export const CheckoutNumbersForPlan = () =>
  CheckoutForPlan({
    queries: {
      useCartQuery: cartApi.useGetForPlanQuery,
      useSaveCartDidsMutation: cartApi.useSaveDidsFromCartMutation,
      useSaveCartBackordersMutation: cartApi.useSaveBackordersFromCartMutation,
      useDeleteDidMutation: cartApi.useRemoveDidFromCartMutation,
      useDeleteBackorderMutation: cartApi.useRemoveBackorderFromCartMutation,
    },
  });

export const CheckoutPSTNUpgrade = () => {
  const queries = {
    useCartQuery: cartApi.useGetForPSTNUpgradeQuery,
    useSaveCartDidsMutation: cartApi.useSaveDidsFromCartMutation,
    useDeleteDidMutation: cartApi.useRemovePSTNUpgradeDidFromCartMutation,
  };
  const { useCartQuery, useSaveCartDidsMutation } = queries;
  const { login } = useSelector((state) => state.auth.user);
  const navigate = useNavigate();
  const { id, type, prefixID } = useParams();
  const queryArg = id ? { id, type, customer: login } : skipToken;
  const cart = useCartQuery(queryArg);

  const profiles =
    planDetailsApi.useGetProfilesAgainstRequirementsQuery(queryArg);

  const saveDids = useSaveCartDidsMutation();

  const isLoading =
    cart.isLoading ||
    cart.isFetching ||
    profiles.isLoading ||
    profiles.isFetching;
  if (isLoading) {
    return <DefaultSpinnerCenter />;
  }
  if (cart.isError || profiles.isError) {
    return <Page500 />;
  }
  if (cart.data && cart.data.length === 0) {
    return navigate('/');
  }

  const didsObjects = {};
  const schemaObjects = {};
  cart.data.forEach((item) => {
    didsObjects[`did-${item.id}`] = {
      ...item,
      profileID: '',
      trunkID: '',
      didServiceType: '',
    };
    schemaObjects[`did-${item.id}`] = Yup.object().shape({
      profileID: Yup.string().label('Profile').required(),
      trunkID: Yup.string().label('Trunk').required(),
      didServiceType: Yup.string().label('DID Service type').required(),
    });
  });
  const validationSchema = Yup.object().shape({
    dids: Yup.object().shape({ ...schemaObjects }),
  });
  const onSubmit = async (values, actions) => {
    const [triggerDids] = saveDids;

    actions.setSubmitting(true);
    function filterObject(obj, callback) {
      return Object.fromEntries(
        Object.entries(obj).filter(([key, val]) => callback(key, val))
      );
    }
    const didData = filterObject(
      values.dids,
      (_, { is_backorder: isBackorder }) => !isBackorder
    );
    const getRedirectPath = () =>
      values.checkout ? '/cart/checkout' : '/order/pstn-upgrade';
    if (Object.entries(didData).length > 0) {
      triggerDids({ data: didData, id, type, prefixID })
        .unwrap()
        .then(() => {
          navigate(getRedirectPath());
        })
        .catch((error) => {
          actions.setErrors(error);
          actions.setSubmitting(false);
        });
    }
  };
  return (
    <>
      <Row style={{ marginBottom: '10px' }}>
        <Col md={12}>
          <Formik
            validationSchema={validationSchema}
            initialValues={{
              checkout: true,
              all: { profileID: '', trunkID: '', didServiceType: '' },
              dids: didsObjects,
            }}
            onSubmit={onSubmit}
          >
            {({ handleSubmit }) => (
              <Form onSubmit={handleSubmit}>
                <ConfigureNumbers data={cart.data} queries={queries} />
                <ApplyToAll />
              </Form>
            )}
          </Formik>
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <ProfilesForPlan data={profiles.data} />
        </Col>
      </Row>
    </>
  );
};
